import { Injectable } from '@angular/core';

import { LoaderInterface } from '@core/interfaces/loader.interface';
import { ComposedLoaderSettings } from '@modules/loaders/interfaces/composed-loader-settings.interface';
import { GradientColorValue } from '@modules/loaders/interfaces/gradient-color-value.interface';
import { LoaderSize } from '@modules/loaders/interfaces/loader-size.interface';
import { WebsiteLoader } from '@modules/loaders/interfaces/website-loader.interface';
import { WebsiteLoaderType } from '@modules/loaders/types/website-loader.type';
import { parseLinearGradient, parseRadialGradient } from '@shared/helpers/gradient-helpers';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class LoaderService {
  private _loaderType: WebsiteLoaderType;
  loader: BehaviorSubject<LoaderInterface> = new BehaviorSubject<LoaderInterface>({display: false});
  composedLoaderSettings: BehaviorSubject<ComposedLoaderSettings> = new BehaviorSubject<ComposedLoaderSettings>(null);

  private linearGradientPosition = {
    x1: '0%',
    y1: '0%',
    x2: '100%',
    y2: '100%',
  };
  private radialGradientPosition = {
    x: '50%',
    y: '50%',
  };
  private isLinearGradient = false;
  private isRadialGradient = false;
  private gradientColorValues: GradientColorValue[] = [];

  loaderSize: Record<WebsiteLoaderType, LoaderSize> = {
    'spinner': {w: 50, h: 50},
    'infinity': {w: 100, h: 50},
    'time': {w: 100, h: 100},
    'bounce-dots': {w: 100, h: 100},
    'rotating-square': {w: 100, h: 100},
    'growing-bars': {w: 100, h: 100},
    'bounce-bars': {w: 100, h: 100},
    'music-bars': {w: 100, h: 100},
  };
  private _color = '#000';

  get color(): string {
    if (this.isLinearGradient) {
      return 'url(#linear)';
    }
    if (this.isRadialGradient) {
      return 'url(#radial)';
    }
    return this._color;
  }

  set color(value: string) {
    this._color = value;
    this.isLinearGradient = value.startsWith('linear-gradient');
    if (this.isLinearGradient) {
      this.setLinearGradient(value);
    }
    this.isRadialGradient = value.startsWith('radial-gradient');
    if (this.isRadialGradient) {
      this.setRadialGradient(value);
    }
  }

  private setLinearGradient(value: string) {
    const linear = parseLinearGradient(value, this.loaderSize[this._loaderType]);
    if (linear !== null) {
      this.gradientColorValues = linear.gradientColorValues;
      this.linearGradientPosition = linear.linearGradientPosition;
    }
  }

  private setRadialGradient(value: string) {
    const radial = parseRadialGradient(value);
    if (radial !== null) {
      this.gradientColorValues = radial.gradientColorValues;
      this.radialGradientPosition = radial.radialGradientPosition;
    }
  }

  displayLoader() {
    const obj: LoaderInterface = {
      display: true,
    };
    this.loader.next(obj);
  }

  hideLoader() {
    const obj: LoaderInterface = {
      display: false,
    };
    this.loader.next(obj);
  }

  setLoaderSettings(loader: WebsiteLoader) {
    this._loaderType = loader.type;
    this.color = loader.color;
    this.updateComposedLoaderSettings();
  }

  updateComposedLoaderSettings() {
    this.composedLoaderSettings.next({
      color: this.color,
      type: this._loaderType,
      size: this.loaderSize[this._loaderType],
      gradientColorValues: this.gradientColorValues,
      linearGradientPosition: this.linearGradientPosition,
      radialGradientPosition: this.radialGradientPosition,
    });
  }
}
